import { useMemo } from 'react';

import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';

import { useAppSelector } from 'store/hooks';
import { useFeatureFlagValue } from 'store/session/hooks';
import {
  getAvailableGroupMembers,
  getUserGroupCollectionId,
  getIsSuperuser,
  getActiveGroup,
} from 'store/session/selectors';

import { useUserGroupCollectionContext } from '../UserGroupCollectionProvider';

export const useVisibleUserGroups = () => {
  const isSuperuser = useAppSelector(getIsSuperuser);
  const availableGroupMembers = useAppSelector(getAvailableGroupMembers);
  const userGroupCollectionId = useAppSelector(getUserGroupCollectionId);
  const activeGroup = useAppSelector(getActiveGroup);
  const { userGroupsById, getUserGroupById } = useUserGroupCollectionContext();
  const isMultisiteEnabled = useFeatureFlagValue(
    featureFlagKeys.isMultisiteFunctionalityAvailable,
  );

  return useMemo(() => {
    if (isMultisiteEnabled && !!userGroupCollectionId) {
      // collection endpoint is intentionally unpartitioned so that badge lookup can resolve sandbox UGs for notifs
      // so we must apply the partition here in the visible groups list
      const activeIsSandbox = !!activeGroup?.isSandbox;
      const inPartition = (group: { isSandbox: boolean }) =>
        !!group.isSandbox === activeIsSandbox;

      if (isSuperuser) {
        return Object.values(userGroupsById).filter(inPartition);
      } else {
        return availableGroupMembers
          .map((groupMember) => getUserGroupById(groupMember.group.id))
          .filter((userGroup) => !!userGroup && inPartition(userGroup));
      }
    } else {
      const visibleGroup = activeGroup
        ? getUserGroupById(activeGroup.id) // match the return type of the other cases
        : null;
      return visibleGroup ? [visibleGroup] : [];
    }
  }, [
    activeGroup,
    availableGroupMembers,
    isMultisiteEnabled,
    isSuperuser,
    userGroupCollectionId,
    userGroupsById,
    getUserGroupById,
  ]);
};
